import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Select, {components} from 'react-select';
import {Button} from '@app/components';
import {getLeadStudents, ReportFilterValues} from '@app/services/reports';
import {getAllActiveSubjects} from '@app/services/subject';
import {getStudentsByFranchiseId} from '@app/services/student';
import {getAllFoundUsVia, getAllGotInTouch, getAllLeadStatus} from '@app/services/lead';

/**
 * Filter bar shared by every report.
 *
 * TWO MODES, switched by a single toggle:
 *
 *   Year (default) — pick a year and a slice of it: a quarter, a half, or all
 *   Custom dates   — pick From/To; the interval is chosen for you
 *
 * Why it is built this way. Chart buckets are calendar periods, so letting
 * someone pick a free date range AND a grouping produced bars that were quietly
 * wrong: From 15 February with "quarter" drew a bar labelled 2025Q1 holding six
 * weeks of data — £56,911 against the true £107,201 — reading as a 47% collapse
 * that never happened.
 *
 * Every option in year mode starts and ends on a calendar boundary, so the bars
 * are always whole months: 3 for a quarter, 6 for a half, 12 for a year. Custom
 * dates has no grouping control to mis-set.
 *
 * Layout follows the existing list pages (lead/ListLeads.tsx,
 * printing-reports/ExceededThreshold.tsx): plain labels, full-size controls,
 * an "All …" first option, and Reset Filter / Export beside the filters.
 * Every control is the same width and sits in one bottom-aligned row, in the
 * same order on every report, so labels, inputs and buttons always line up.
 *
 * Each report turns on only the filters its data actually holds — see `show`.
 * Option lists are loaded here rather than in every page.
 */

/** Chips shown in the franchise box before the rest collapse into "+N more". */
const MAX_FRANCHISE_CHIPS = 1;

/**
 * Keeps the multi-select one line tall.
 *
 * Every filter in the row is bottom-aligned, so a box that grows with each chip
 * pushes the other controls down and leaves a gap above them. Showing the first
 * few chips and a count keeps it the same height as its neighbours; the full
 * selection is still visible when the menu is open.
 */
const FranchiseValueContainer = ({children, ...props}: any) => {
  // react-select passes [selected values, input]; the values are themselves an
  // array, so this must not go through React.Children.toArray (it would flatten).
  const [chips, input] = children as [React.ReactNode, React.ReactNode];
  const selected = props.getValue().length;
  const shown = Array.isArray(chips) ? chips.slice(0, MAX_FRANCHISE_CHIPS) : chips;
  const extra = selected - MAX_FRANCHISE_CHIPS;

  return (
    <components.ValueContainer {...props}>
      {shown}
      {extra > 0 && (
        <span
          className="text-muted small ml-1 text-nowrap"
          title={props.getValue().map((o: any) => o.label).join(', ')}>
          +{extra} more
        </span>
      )}
      {input}
    </components.ValueContainer>
  );
};

const FRANCHISE_SELECT_STYLES = {
  valueContainer: (base: any) => ({...base, flexWrap: 'nowrap', overflow: 'hidden'}),
  // The chip keeps its natural width up to most of the box; only a very long
  // name is cut short, never a normal one.
  multiValue: (base: any) => ({...base, maxWidth: '70%', flexShrink: 0})
};

export interface FranchiseOption {
  value: number;
  label: string;
}

/** Mirrors YEAR_PERIODS in services/reports/filters.js. */
export const YEAR_PERIODS = [
  {value: 'q1', label: 'Quarter 1 (Jan–Mar)'},
  {value: 'q2', label: 'Quarter 2 (Apr–Jun)'},
  {value: 'q3', label: 'Quarter 3 (Jul–Sep)'},
  {value: 'q4', label: 'Quarter 4 (Oct–Dec)'},
  {value: 'h1', label: 'First half (Jan–Jun)'},
  {value: 'h2', label: 'Second half (Jul–Dec)'},
  {value: 'full', label: 'Full year'}
];

/** Values mirror the allowed lists in services/reports/filters.js (fms-api). */
const ITEM_TYPES = [
  {value: 'set', label: 'Set'},
  {value: 'block', label: 'Block'},
  {value: 'module', label: 'Module'},
  // 'handbook' is single handbooks only; combined handbooks are their own value.
  {value: 'handbook', label: 'Handbook (single)'},
  {value: 'handbook_combined', label: 'Handbook (combined)'},
  {value: 'handbook_suppliment', label: 'Handbook supplement'},
  {value: 'other_item', label: 'Other item'}
];

const ORDER_STATUSES = [
  {value: 'dispatched', label: 'Dispatched'},
  {value: 'sent', label: 'Sent'},
  {value: 'acknowledged', label: 'Acknowledged'},
  {value: 'cancelled', label: 'Cancelled'}
];

/** Labels match the payment source filter on order-form/ListOrders.tsx. */
const PAYMENT_SOURCES = [
  {value: 'stripe', label: 'Regular Orders'},
  {value: 'paylater', label: 'Pay Later Orders'},
  {value: 'in-house', label: 'In-house Orders'},
  {value: 'launchpack', label: 'Launchpack Orders'}
];

/** Same three groups as the Student Subject Orders payment chart. */
const PAYMENT_STATUSES = [
  {value: 'paid', label: 'Paid'},
  {value: 'unpaid', label: 'Unpaid'},
  {value: 'no_order', label: 'No order'}
];

const PRINT_TYPES = [
  {value: 'setwork', label: 'Set work'},
  {value: 'adhoc', label: 'Adhoc'},
  {value: 'setwork_handbook', label: 'Handbook'}
];

const WORK_ORDER_STATUSES = [
  {value: 'pending', label: 'Pending'},
  {value: 'on_hold', label: 'On hold'},
  {value: 'added_to_cart', label: 'Added to cart'},
  {value: 'exclude_forever', label: 'Excluded'}
];

/**
 * Won and lost statuses — a lead cannot be stuck at these. Mirrors
 * ENROLLED_SLUGS and LOST_SLUGS in services/reports/leads-common.js, which the
 * stalled-leads list already excludes.
 */
const FINISHED_LEAD_SLUGS = [
  'enrolled_and_paid',
  'confirmed_and_paid',
  'enrolled_payment_pending',
  'lost',
  'no_response'
];

/** Every control takes one cell: four to a line on desktop. */
const CELL = 'col-lg-3 col-md-4 col-sm-6 mb-3';

interface ShowOptions {
  franchise?: boolean;
  subject?: boolean;
  student?: boolean;
  itemType?: boolean;
  orderStatus?: boolean;
  paymentSource?: boolean;
  paymentStatus?: boolean;
  printType?: boolean;
  workOrderStatus?: boolean;
  /** Growth reports: enquiry child, not an enrolled student. */
  leadStudent?: boolean;
  /** Lead Funnel: current status. */
  leadStatus?: boolean;
  /** Lead Velocity: current status, unfinished stages only. */
  stuckAt?: boolean;
  foundVia?: boolean;
  gotInTouch?: boolean;
}

interface Props {
  values: ReportFilterValues;
  onChange: (next: ReportFilterValues) => void;
  onApply: () => void;
  /** Receives the default filters; the page sets and applies them. */
  onReset?: (defaults: ReportFilterValues) => void;
  onExport?: () => void;
  exporting?: boolean;
  franchises?: FranchiseOption[];
  years?: number[];
  /** Interval the API chose, shown in custom-date mode. */
  intervalLabel?: string;
  show?: ShowOptions;
  /** Optional per-report override of the order status options. */
  orderStatusOptions?: Option[];
  disabled?: boolean;
}

interface Option {
  value: string | number;
  label: string;
}

/** A plain select with an "All …" first option, as on ExceededThreshold.tsx. */
const ListSelect = ({
  label,
  allLabel,
  options,
  value,
  onSelect
}: {
  label: string;
  allLabel: string;
  options: Option[];
  value?: string | number;
  onSelect: (value: string) => void;
}) => (
  <div className={CELL}>
    <label>{label}</label>
    <select
      className="form-control"
      value={value ?? ''}
      onChange={(e) => onSelect(e.target.value)}>
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

/** Loads {success, collections} into options; failures leave the list empty. */
const loadOptions = async (
  fetcher: () => Promise<any>,
  map: (row: any) => Option,
  setter: (options: Option[]) => void
) => {
  const response = await fetcher();
  if (response?.success === true && Array.isArray(response.collections)) {
    setter(response.collections.map(map));
  }
};

const ReportFilters = ({
  values,
  onChange,
  onApply,
  onReset,
  onExport,
  exporting,
  franchises = [],
  years = [],
  intervalLabel,
  show,
  orderStatusOptions,
  disabled
}: Props) => {
  const showFranchise = show?.franchise !== false;
  const customDates = values.mode === 'range';

  const [subjects, setSubjects] = useState<Option[]>([]);
  const [students, setStudents] = useState<Option[]>([]);
  const [leadStudents, setLeadStudents] = useState<Option[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<Option[]>([]);
  const [foundVia, setFoundVia] = useState<Option[]>([]);
  const [gotInTouch, setGotInTouch] = useState<Option[]>([]);

  // Student lists are only offered for a single franchise: across every
  // franchise they would be thousands of names in one dropdown.
  const selectedFranchises = Array.isArray(values.franchiseId)
    ? values.franchiseId.map(Number)
    : values.franchiseId
      ? [Number(values.franchiseId)]
      : [];
  const singleFranchiseId =
    selectedFranchises.length === 1 ? selectedFranchises[0] : null;

  useEffect(() => {
    if (show?.subject) {
      loadOptions(getAllActiveSubjects, (s) => ({value: s.id, label: s.name}), setSubjects);
    }
    if (show?.leadStatus || show?.stuckAt) {
      // Read by slug — ids differ per environment.
      loadOptions(getAllLeadStatus, (s) => ({value: s.slug, label: s.name}), setLeadStatuses);
    }
    if (show?.foundVia) {
      loadOptions(getAllFoundUsVia, (s) => ({value: s.id, label: s.name}), setFoundVia);
    }
    if (show?.gotInTouch) {
      loadOptions(getAllGotInTouch, (s) => ({value: s.id, label: s.name}), setGotInTouch);
    }
  }, [show?.subject, show?.leadStatus, show?.stuckAt, show?.foundVia, show?.gotInTouch]);

  useEffect(() => {
    setStudents([]);
    if (!show?.student || !singleFranchiseId) return;
    loadOptions(
      () => getStudentsByFranchiseId(singleFranchiseId),
      (s) => ({value: s.id, label: s.student_name}),
      setStudents
    );
  }, [show?.student, singleFranchiseId]);

  useEffect(() => {
    setLeadStudents([]);
    if (!show?.leadStudent || !singleFranchiseId) return;
    loadOptions(
      () => getLeadStudents(singleFranchiseId),
      (s) => ({value: s.id, label: s.name || `Lead student #${s.id}`}),
      setLeadStudents
    );
  }, [show?.leadStudent, singleFranchiseId]);

  const set = (patch: Partial<ReportFilterValues>) =>
    onChange({...values, ...patch, page: 1});

  /** Empty string from an "All …" option clears the filter. */
  const setText = (key: keyof ReportFilterValues) => (value: string) =>
    set({[key]: value || undefined} as Partial<ReportFilterValues>);

  const setNumber = (key: keyof ReportFilterValues) => (value: string) =>
    set({[key]: value ? Number(value) : undefined} as Partial<ReportFilterValues>);

  // Switching clears the other mode's inputs, so a stale From date can never
  // linger behind a year selection.
  const toggleMode = (useCustom: boolean) =>
    onChange(
      useCustom
        ? {...values, mode: 'range', year: undefined, period: undefined, page: 1}
        : {
            ...values,
            mode: 'year',
            from: undefined,
            to: undefined,
            year: values.year || years[0],
            period: values.period || 'full',
            page: 1
          }
    );

  // The same defaults every report page starts from.
  const reset = () =>
    onReset &&
    onReset({
      mode: 'year',
      year: years[0],
      period: 'full',
      page: 1,
      limit: values.limit
    });

  /** A searchable student picker, enabled once one franchise is chosen. */
  const studentPicker = (
    label: string,
    options: Option[],
    value: number | undefined,
    onPick: (value: number | undefined) => void
  ) => (
    <div className={CELL}>
      <label>{label}</label>
      <Select
        isClearable
        options={options}
        isDisabled={!singleFranchiseId}
        placeholder={
          singleFranchiseId ? 'Search By Student' : 'Select one franchise first'
        }
        className="basic-single"
        classNamePrefix="select"
        value={options.find((o) => o.value === value) || null}
        onChange={(selected: any) => onPick(selected ? selected.value : undefined)}
      />
    </div>
  );

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center">
        <h3 className="card-title text-capitalize">Filter and export</h3>
        <Link className="btn btn-default btn-sm ml-auto" to="/reports">
          <i className="fas fa-arrow-left mr-1" /> Back to Reports
        </Link>
      </div>

      <div className="card-body">
        <div className="custom-control custom-switch mb-3">
          <input
            type="checkbox"
            className="custom-control-input"
            id="useCustomDates"
            checked={customDates}
            onChange={(e) => toggleMode(e.target.checked)}
          />
          <label className="custom-control-label" htmlFor="useCustomDates">
            Use custom date range
          </label>
        </div>

        <div className="row align-items-end">
          {!customDates ? (
            <>
              <div className={CELL}>
                <label>Year</label>
                <select
                  className="form-control"
                  value={values.year ?? ''}
                  onChange={(e) => set({year: Number(e.target.value)})}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className={CELL}>
                <label>Period</label>
                <select
                  className="form-control"
                  value={values.period || 'full'}
                  onChange={(e) => set({period: e.target.value as any})}>
                  {YEAR_PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className={CELL}>
                <label>From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={values.from || ''}
                  onChange={(e) => set({from: e.target.value})}
                />
              </div>
              <div className={CELL}>
                <label>To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={values.to || ''}
                  onChange={(e) => set({to: e.target.value})}
                />
              </div>
            </>
          )}

          {showFranchise && (
            <div className={CELL}>
              <label>Franchise</label>
              <Select
                isClearable
                isMulti
                options={franchises}
                placeholder="Search By Franchise"
                className="basic-single"
                classNamePrefix="select"
                components={{ValueContainer: FranchiseValueContainer}}
                styles={FRANCHISE_SELECT_STYLES}
                value={franchises.filter((f) =>
                  Array.isArray(values.franchiseId)
                    ? values.franchiseId.map(Number).includes(f.value)
                    : false
                )}
                // A chosen student belongs to one franchise, so it is cleared
                // whenever the franchise selection changes.
                onChange={(selected: any) =>
                  set({
                    franchiseId: (selected || []).map((s: FranchiseOption) => s.value),
                    studentId: undefined,
                    leadStudentId: undefined
                  })
                }
              />
            </div>
          )}

          {show?.itemType && (
            <ListSelect
              label="Item Type"
              allLabel="All Item Types"
              options={ITEM_TYPES}
              value={values.itemType}
              onSelect={setText('itemType')}
            />
          )}
          {show?.subject && (
            <ListSelect
              label="Subject"
              allLabel="All Subjects"
              options={subjects}
              value={values.subjectId}
              onSelect={setNumber('subjectId')}
            />
          )}
          {show?.student &&
            studentPicker('Student', students, values.studentId, (v) =>
              set({studentId: v})
            )}
          {show?.leadStudent &&
            studentPicker('Student', leadStudents, values.leadStudentId, (v) =>
              set({leadStudentId: v})
            )}
          {show?.leadStatus && (
            <ListSelect
              label="Status"
              allLabel="All Statuses"
              options={leadStatuses}
              value={values.leadStatus}
              onSelect={setText('leadStatus')}
            />
          )}
          {show?.stuckAt && (
            <ListSelect
              label="Stuck At"
              allLabel="All Stages"
              options={leadStatuses.filter(
                (s) => !FINISHED_LEAD_SLUGS.includes(String(s.value))
              )}
              value={values.leadStatus}
              onSelect={setText('leadStatus')}
            />
          )}
          {show?.foundVia && (
            <ListSelect
              label="Source"
              allLabel="All Sources"
              options={foundVia}
              value={values.foundViaId}
              onSelect={setNumber('foundViaId')}
            />
          )}
          {show?.gotInTouch && (
            <ListSelect
              label="Contact Method"
              allLabel="All Contact Methods"
              options={gotInTouch}
              value={values.gotInTouchId}
              onSelect={setNumber('gotInTouchId')}
            />
          )}
          {show?.orderStatus && (
            <ListSelect
              label="Order Status"
              allLabel="All Statuses"
              options={orderStatusOptions || ORDER_STATUSES}
              value={values.orderStatus}
              onSelect={setText('orderStatus')}
            />
          )}
          {show?.paymentSource && (
            <ListSelect
              label="Payment Source"
              allLabel="All Orders"
              options={PAYMENT_SOURCES}
              value={values.paymentSource}
              onSelect={setText('paymentSource')}
            />
          )}
          {show?.paymentStatus && (
            <ListSelect
              label="Payment"
              allLabel="All Payments"
              options={PAYMENT_STATUSES}
              value={values.paymentStatus}
              onSelect={setText('paymentStatus')}
            />
          )}
          {show?.printType && (
            <ListSelect
              label="Print Type"
              allLabel="All Print Types"
              options={PRINT_TYPES}
              value={values.printType}
              onSelect={setText('printType')}
            />
          )}
          {show?.workOrderStatus && (
            <ListSelect
              label="Status"
              allLabel="All Statuses"
              options={WORK_ORDER_STATUSES}
              value={values.workOrderStatus}
              onSelect={setText('workOrderStatus')}
            />
          )}

          {/* Always last, pushed to the right of its line. */}
          <div className="col-auto ml-auto mb-3">
            <Button
              type="button"
              className="mr-2"
              disabled={disabled}
              onClick={onApply}>
              Apply
            </Button>
            {onReset && (
              <Button
                type="button"
                theme="warning"
                className="mr-2"
                disabled={disabled}
                onClick={reset}>
                Reset Filter
              </Button>
            )}
            {onExport && (
              <Button
                type="button"
                theme="success"
                isLoading={exporting}
                disabled={disabled}
                onClick={onExport}>
                <i className="far fa-file-excel mr-1" /> Export
              </Button>
            )}
          </div>
        </div>

        {customDates && intervalLabel && (
          <small className="text-muted d-block">
            Showing <strong>{intervalLabel.toLowerCase()}</strong> totals, chosen
            from the length of the range. These are calendar periods, so a range
            starting mid-month shows a part-month at each end — switch the toggle
            off for whole quarters and halves.
          </small>
        )}
      </div>
    </div>
  );
};

export default ReportFilters;
